import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import ReactGA from "react-ga4";
import { FaApple } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { QRCodeSVG } from "qrcode.react";
import {
    Box, Button, Flex, IconButton, Popover, PopoverArrow,
    PopoverBody, PopoverContent, PopoverTrigger, Text, useColorModeValue,
} from "@chakra-ui/react";
import { appStoreUrl, ease } from "../../constants";
import useResponsiveLayout from "../../hooks/useResponsiveLayout";

const BANNER_H = "52px";

const AppBanner = () => {
    const location = useLocation();
    const [dismissed, setDismissed] = useState(false);
    const { isDesktopLayout } = useResponsiveLayout();

    const bg     = useColorModeValue("rgba(245,237,224,0.95)", "rgba(12,7,2,0.94)");
    const border = useColorModeValue("rgba(139,62,18,0.12)", "rgba(200,169,126,0.08)");
    const text   = useColorModeValue("#1a0c04", "#e8d9c4");
    const dim    = useColorModeValue("#6a4020", "#a89880");
    const accent = useColorModeValue("#8b3e12", "#c8a97e");
    const btnBg  = useColorModeValue("rgba(139,62,18,0.08)", "rgba(200,169,126,0.08)");
    const btnHover = useColorModeValue("rgba(139,62,18,0.15)", "rgba(200,169,126,0.15)");
    const qrBg   = useColorModeValue("#fdfaf5", "#1a1209");

    const visible = location.pathname !== "/" && !dismissed && isDesktopLayout;

    // Explore cards read this. Safari’s URL bar is handled by 100dvh on the app shell —
    // only our fixed app banner needs an explicit inset (matched to its enter delay).
    // Banner is desktop-only; keep the CSS var cleared on mobile.
    useEffect(() => {
        if (!visible) {
            document.documentElement.style.setProperty("--kuthe-app-banner", "0px");
            return undefined;
        }
        const t = setTimeout(() => {
            document.documentElement.style.setProperty("--kuthe-app-banner", BANNER_H);
        }, 1200);
        return () => {
            clearTimeout(t);
            document.documentElement.style.setProperty("--kuthe-app-banner", "0px");
        };
    }, [visible]);

    const storeUrl = appStoreUrl("app_banner");

    if (!visible) return null;

    const trackAppStoreClick = () => {
        ReactGA.event({ category: "App", action: "App Store Click", label: "App Banner" });
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 64, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 64, opacity: 0 }}
                transition={{ duration: 0.35, ease, delay: 1.2 }}
                style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900 }}
            >
                <Box
                    bg={bg}
                    sx={{ backdropFilter: "blur(16px) saturate(1.4)" }}
                    borderTop="1px solid"
                    borderColor={border}
                    px={{ base: 4, md: 8 }}
                    h={BANNER_H}
                >
                    <Flex align="center" justify="space-between" h="full" maxW="900px" mx="auto" gap={3}>

                        {/* Label */}
                        <Text fontSize={{ base: "12px", md: "13px" }} color={dim} fontFamily="body" letterSpacing="0.01em" flexShrink={0} noOfLines={1}>
                            <Box as="span" color={text} fontWeight={500}>Kuthé</Box> is now on iOS
                        </Text>

                        <Flex align="center" gap={2} flexShrink={0}>
                            {isDesktopLayout ? (
                                <Popover trigger="hover" placement="top" closeOnBlur={false}>
                                    <PopoverTrigger>
                                        <Button
                                            as="a" href={storeUrl} target="_blank" rel="noopener noreferrer"
                                            onClick={trackAppStoreClick}
                                            leftIcon={<FaApple size={13} />} size="sm" bg={btnBg} color={accent}
                                            borderRadius="full" px={4} fontSize="12px" fontFamily="body"
                                            fontWeight={500} border="1px solid" borderColor={border} _hover={{ bg: btnHover }}
                                        >
                                            Get the app
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent w="auto" border="1px solid" borderColor={border} bg={qrBg} borderRadius="14px" p={3} boxShadow="0 8px 32px rgba(0,0,0,0.18)">
                                        <PopoverArrow bg={qrBg} borderColor={border} />
                                        <PopoverBody p={0}>
                                            <Flex direction="column" align="center" gap={2}>
                                                <Box p={2} bg="white" borderRadius="8px">
                                                    <QRCodeSVG value={storeUrl} size={100} fgColor="#1a0c04" bgColor="#ffffff" level="M" />
                                                </Box>
                                                <Text fontSize="10px" color={dim} letterSpacing="0.08em" textTransform="uppercase" fontFamily="body">
                                                    Scan to download
                                                </Text>
                                            </Flex>
                                        </PopoverBody>
                                    </PopoverContent>
                                </Popover>
                            ) : (
                                <Button
                                    as="a" href={storeUrl} target="_blank" rel="noopener noreferrer"
                                    onClick={trackAppStoreClick}
                                    leftIcon={<FaApple size={13} />} size="sm" bg={btnBg} color={accent}
                                    borderRadius="full" px={4} fontSize="12px" fontFamily="body"
                                    fontWeight={500} border="1px solid" borderColor={border} _hover={{ bg: btnHover }}
                                >
                                    Get the app
                                </Button>
                            )}

                            <IconButton
                                icon={<MdClose size={14} />}
                                aria-label="Dismiss"
                                size="xs"
                                variant="ghost"
                                color={dim}
                                borderRadius="full"
                                minW={6}
                                h={6}
                                _hover={{ color: text, bg: btnBg }}
                                onClick={() => setDismissed(true)}
                            />
                        </Flex>
                    </Flex>
                </Box>
            </motion.div>
        </AnimatePresence>
    );
};

export default AppBanner;
